import { useEffect, useReducer, useRef } from 'react'
import RouterPromptStay from 'app/router/RouterPromptStay'

import ButtonWithDropdown from 'common/components/ButtonWithDropdown'
import ConfirmationModal from 'common/components/confirmationModal'
import ErrorPage from 'common/components/error/ErrorPage'
import Footer from 'common/components/layout/Footer'
import PageContainer from 'common/components/PageContainer'
import PageHeader from 'common/components/PageHeader'
import PageTitle from 'common/components/PageTitle'
import Spinner from 'common/components/spinner'
import { errorToast, successToast } from 'common/components/toastNotification'
import Widget from 'common/components/Widget'
import WidgetHeader from 'common/components/WidgetHeader'
import { useDocumentTitle } from 'common/hooks/custom'
import { useAppSelector } from 'common/hooks/redux'
import { Add as AddIcon } from 'common/icons'

import useGetCustomerAttributesQuery from './api/queries/useGetCustomerAttributesQuery'
import useGetProductCatalogQuery from './api/queries/useGetProductCatalogQuery'
import CsvCustomerPreview from './components/CsvCustomerPreview'
import {
  csvCustomerInitialState,
  csvCustomerReducer,
  CsvCustomerState,
} from './components/csvCustomerReducer'
import CsvProductPreview from './components/CsvProductPreview'
import {
  csvProductInitialState,
  csvProductReducer,
} from './components/csvProductReducer'
import CsvUploader from './components/CsvUploader'
import CustomerAttributesTable from './components/CustomerAttributesTable'
import ProductCategoriesTable from './components/ProductCategoriesTable'
import SourceButton from './components/SourceButton'
import { Uploader } from './upload'

const DataSources = () => {
  useDocumentTitle('Data sources| Phrasee')
  const accountId = useAppSelector((state) => state.authStates.accountId)
  const dataIngestionBaseUrl = useAppSelector(
    (state) => state.authStates.accounts
  )?.find((account) => account.id === accountId)?.data_ingestion_base_url

  if (!dataIngestionBaseUrl) {
    throw new Error('The data ingestion base URL is not defined')
  }

  const customerAttributesQuery = useGetCustomerAttributesQuery({
    baseUrl: dataIngestionBaseUrl,
    accountId,
  })
  const productCatalogQuery = useGetProductCatalogQuery({
    baseUrl: dataIngestionBaseUrl,
  })

  const csvUploaderCustomerRef = useRef<{ open: () => void }>(null)
  const csvUploaderProductRef = useRef<{ open: () => void }>(null)

  const fileRef = useRef<File | undefined>(undefined)
  const uploaderRef = useRef<Uploader | undefined>(undefined)

  const [csvCustomerState, csvCustomerDispatch] = useReducer(
    csvCustomerReducer,
    csvCustomerInitialState
  )
  const [csvProductState, csvProductDispatch] = useReducer(
    csvProductReducer,
    csvProductInitialState
  )

  const csvUploaderStateRef = useRef<CsvCustomerState | null>(null)
  useEffect(() => {
    csvUploaderStateRef.current = csvCustomerState
  }, [csvCustomerState])

  useEffect(() => {
    return () => {
      uploaderRef.current?.abort()
    }
  }, [])

  if (customerAttributesQuery.isError || productCatalogQuery.isError) {
    return <ErrorPage />
  }

  return (
    <PageContainer className="px-6">
      {customerAttributesQuery.isLoading || productCatalogQuery.isLoading ? (
        <Spinner />
      ) : (
        <>
          <PageHeader className="mb-8">
            <div className="flex justify-between flex-1">
              <div className="pt-9">
                <PageTitle title="Data sources" />
                <div className="text-coolGray-400">
                  Configure the below settings to let Phrasee know what we
                  should or should not use in the generation of your content.
                </div>
              </div>
            </div>
          </PageHeader>
          <div className="flex flex-col gap-8">
            <Widget>
              <WidgetHeader
                title="Customer attributes"
                subtitle="Customer attributes description and what we need to go here along with a help file link"
                actions={
                  <ButtonWithDropdown
                    overlayClassName="capitalize"
                    variant="primary"
                    suffixIcon={false}
                    disabled={
                      csvCustomerState.progressInPercentage !== undefined
                    }
                    options={[
                      {
                        key: 'csv_file',
                        label: 'CSV file',
                        onClick: () => {
                          csvUploaderCustomerRef.current?.open()
                        },
                        isDisabled: false,
                      },
                      {
                        key: 'database',
                        label: 'Database',
                        onClick: () => {},
                        isDisabled: true,
                      },
                      {
                        key: 'api_integration',
                        label: 'API Integration',
                        onClick: () => {},
                        isDisabled: true,
                      },
                    ]}
                    prefixIcon={
                      <AddIcon
                        className="text-white h-4 w-4"
                        isDefaultColor={false}
                      />
                    }
                  >
                    Attributes
                  </ButtonWithDropdown>
                }
              />

              <div className="flex gap-6 items-stretch">
                <CsvUploader
                  ref={csvUploaderCustomerRef}
                  fileName={csvCustomerState.fileName}
                  progressInPercentage={csvCustomerState.progressInPercentage}
                  onStartUpload={(file) => {
                    fileRef.current = file
                  }}
                  onUpload={({ data, headers, fileName }) => {
                    csvCustomerDispatch({
                      type: 'upload',
                      data,
                      headers,
                      fileName,
                    })
                  }}
                  description="Max 100 million customer records or 10GB file size"
                />
                <SourceButton
                  title="Database"
                  subtitle="Pull customer attributes directly from your database"
                  emoji="👾"
                  isDisabled
                />
                <SourceButton
                  title="API integration"
                  subtitle="Pull customer data from your API"
                  emoji="🦚"
                  isDisabled
                />
              </div>
              <CustomerAttributesTable
                baseUrl={dataIngestionBaseUrl}
                isLoading={customerAttributesQuery.isLoading}
                customerAttributes={customerAttributesQuery.data}
              />
            </Widget>
            <Widget>
              <WidgetHeader
                title="Product categories"
                subtitle="Product categories description and what we need to go here along with a help file link"
                actions={
                  <ButtonWithDropdown
                    overlayClassName="capitalize"
                    variant="primary"
                    suffixIcon={false}
                    options={[
                      {
                        key: 'csv_file',
                        label: 'CSV file',
                        onClick: () => {
                          csvUploaderProductRef.current?.open()
                        },
                        isDisabled: false,
                      },
                      {
                        key: 'database',
                        label: 'Database',
                        onClick: () => {},
                        isDisabled: true,
                      },
                      {
                        key: 'api_integration',
                        label: 'API Integration',
                        onClick: () => {},
                        isDisabled: true,
                      },
                    ]}
                    prefixIcon={
                      <AddIcon
                        className="text-white h-4 w-4"
                        isDefaultColor={false}
                      />
                    }
                  >
                    Categories
                  </ButtonWithDropdown>
                }
              />
              <div className="flex gap-6 items-stretch">
                <CsvUploader
                  ref={csvUploaderProductRef}
                  fileName={undefined}
                  progressInPercentage={undefined}
                  onStartUpload={(file) => {
                    fileRef.current = file
                  }}
                  onUpload={({ data, headers, fileName }) => {
                    csvProductDispatch({
                      type: 'upload',
                      data,
                      headers,
                      fileName,
                    })
                  }}
                />
                <SourceButton
                  title="Database"
                  subtitle="Pull product catalog directly from your database"
                  emoji="👾"
                  isDisabled
                />
                <SourceButton
                  title="API integration"
                  subtitle="Pull product catalog from your API"
                  emoji="🦚"
                  isDisabled
                />
              </div>

              <ProductCategoriesTable baseUrl={dataIngestionBaseUrl} />
            </Widget>
          </div>

          <Footer />
          <CsvCustomerPreview
            isOpen={csvCustomerState.isPreviewOpen}
            fileName={csvCustomerState.fileName}
            headers={csvCustomerState.headers}
            data={csvCustomerState.data ?? []}
            onCancel={() => {
              csvCustomerDispatch({ type: 'cancelPreview' })
            }}
            onImport={(values) => {
              csvCustomerDispatch({
                type: 'clickImport',
              })
              const file = fileRef.current
              if (file) {
                uploaderRef.current = new Uploader({
                  baseUrl: dataIngestionBaseUrl,
                  fileName: values.fileName,
                  file: file,
                  accountId,
                })
                  .onProgress(({ percentage: newPercentage }) => {
                    if (
                      newPercentage === 100 &&
                      csvUploaderStateRef.current !== null &&
                      csvUploaderStateRef.current.data
                    ) {
                      successToast('File successfully uploaded')
                      csvCustomerDispatch({ type: 'uploadCompleted' })
                    } else if (newPercentage !== 100) {
                      csvCustomerDispatch({
                        type: 'uploadProgress',
                        percentage: newPercentage,
                      })
                    }
                  })
                  .onError(() => {
                    csvCustomerDispatch({ type: 'errorUpload' })
                    errorToast(
                      'Something went wrong while uploading the file. Please try again or contact support.'
                    )
                  })
                uploaderRef.current.start({
                  source: 'customer',
                  data: { customerIdString: values.customerIdColumn },
                })
              }
            }}
          />
          <CsvProductPreview
            isOpen={csvProductState.isPreviewOpen}
            fileName={csvProductState.fileName}
            headers={csvProductState.headers}
            data={csvProductState.data ?? []}
            onCancel={() => {
              csvProductDispatch({ type: 'cancelPreview' })
            }}
            onImport={(values) => {
              csvProductDispatch({
                type: 'clickImport',
              })
              const file = fileRef.current
              if (file) {
                uploaderRef.current = new Uploader({
                  baseUrl: dataIngestionBaseUrl,
                  fileName: values.fileName,
                  file: file,
                  accountId,
                })
                  .onProgress(({ percentage: newPercentage }) => {
                    if (
                      newPercentage === 100 &&
                      csvUploaderStateRef.current !== null &&
                      csvUploaderStateRef.current.data
                    ) {
                      successToast('File successfully uploaded')
                      csvProductDispatch({ type: 'uploadCompleted' })
                    } else if (newPercentage !== 100) {
                      csvProductDispatch({
                        type: 'uploadProgress',
                        percentage: newPercentage,
                      })
                    }
                  })
                  .onError(() => {
                    csvProductDispatch({ type: 'errorUpload' })
                    errorToast(
                      'Something went wrong while uploading the file. Please try again or contact support.'
                    )
                  })
                uploaderRef.current.start({
                  source: 'product',
                  data: {
                    productIdColumnName: values.productIdColumn,
                    productNameColumnName: values.productNameColumn,
                    separatedBy: values.dataSeparator,
                    categoryColumnName: values.categoryColumn,
                    categoryDelimiter: values.delimiter,
                    categoryLevelsColumnNames: values.categoryLevel,
                  },
                })
              }
            }}
          />
        </>
      )}
      <ConfirmationModal
        onCancel={() => {
          csvCustomerDispatch({ type: 'cancelConfirmationPreview' })
        }}
        onConfirm={() => {
          csvCustomerDispatch({ type: 'closeConfirmationPreview' })
          fileRef.current = undefined
        }}
        open={csvCustomerState.isConfirmationPreviewOpen}
        confirmationText="Are you sure you want to leave without importing the data?"
        cancelButtonText="Stop, don’t do it"
        confirmButtonText="Close"
        title="Are you sure?"
      />

      <ConfirmationModal
        onCancel={() => {
          csvProductDispatch({ type: 'cancelConfirmationPreview' })
        }}
        onConfirm={() => {
          csvProductDispatch({ type: 'closeConfirmationPreview' })
          fileRef.current = undefined
        }}
        open={csvProductState.isConfirmationPreviewOpen}
        confirmationText="Are you sure you want to leave without importing the data?"
        cancelButtonText="Stop, don’t do it"
        confirmButtonText="Close"
        title="Are you sure?"
      />

      <RouterPromptStay
        shouldShow={
          csvCustomerState.progressInPercentage !== undefined ||
          csvProductState.progressInPercentage !== undefined
        }
        onOK={() => Promise.resolve(true)}
        confirmationText="If you navigate away from this page, the current file upload process will be interrupted."
      />
    </PageContainer>
  )
}

export default DataSources
